<script setup lang="ts">
import { computed, defineProps, ref, withDefaults } from 'vue';
import { onClickOutside } from '@vueuse/core/index';
import DraftHeaderMenu from '@/dashboard/vue/components/draft/_components/DraftHeaderMenu.vue';
import IconButton from '@/dashboard/vue/components/common/IconButton.vue';
import { Draft } from '@/dashboard/types/draft';
import { useAppStore } from '@/dashboard/store/appStore';
import LockIcon from '@/dashboard/vue/components/icons/LockIcon.vue';
import TeamIcon from '@/dashboard/vue/components/icons/TeamIcon.vue';
import { useBreadcrumbsSim } from './useBreadcrumbsSim';

const props = withDefaults(
  defineProps<{
    draft: Draft | null;
    disabled: boolean;
    showMenuButton: boolean;
  }>(),
  {
    draft: null,
    disabled: true,
    showMenuButton: true,
  }
);

const showTopMenuBtn = ref(null);
const topHeaderMenuRef = ref(null);
const showHeaderMenu = ref(false);

const appStore = useAppStore();
const sim = useBreadcrumbsSim();

// Крошки показываются только владельцу драфта — так в макетах: остальные видят
// плашку с одним логотипом и «...».
const draftView = computed(() => sim.draftView(props.draft));
const viewerId = computed(() => sim.viewerId(appStore.userId, props.draft));
const isOwner = computed(() => !!draftView.value && draftView.value.owner_id === viewerId.value);

// Клик по воркспейсу и по директории ведёт в одно и то же место — в директорию.
const directoryLink = computed(() => (draftView.value?.project_short_link ? `/d/${draftView.value.project_short_link}` : null));
const showWorkspaceCrumb = computed(() => !!draftView.value?.workspace_name && !!directoryLink.value);
const showProjectCrumb = computed(() => !!draftView.value?.project_name && !!directoryLink.value);

function showTopMenu() {
  if (!props.disabled) {
    showHeaderMenu.value = !showHeaderMenu.value;
  }
}

onClickOutside(showTopMenuBtn, (event) => (showHeaderMenu.value = false), { ignore: [topHeaderMenuRef] });
</script>

<template>
  <div
    class="absolute left-xs right-auto top-xs z-[1001] items-center justify-start gap-m max-tablet:left-s max-tablet:right-s max-tablet:top-s laptop:flex laptop:h-xxl laptop:rounded-12 laptop:border laptop:border-[#DBDBDB]/50 laptop:bg-[#ECECEC]/80 laptop:py-xs laptop:pl-s laptop:pr-xs laptop:shadow-[inset_0_0_12px_rgba(255,255,255,0.8)] laptop:backdrop-blur-[44px] laptop:dark:border-[#2B2B2B]/50 laptop:dark:bg-[#1C1C1C]/80 laptop:dark:shadow-[inset_0_0_12px_rgba(255,255,255,0.1)]"
  >
    <div class="flex h-m w-[78px] items-center justify-between">
      <a href="/" class="">
        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
          <g clip-path="url(#clip0_3989_10946)">
            <path
              class="fill-black dark:fill-gray-700"
              d="M11.7265 23.9999C18.4117 23.9999 23.8311 18.5805 23.8311 11.8953C23.8311 5.99753 19.6131 1.08489 14.0291 0.00952148C13.5905 4.16911 10.6353 12.6406 0.0839844 15.219C1.52835 20.2882 6.19398 23.9999 11.7265 23.9999Z"
            />
            <path
              class="fill-black dark:fill-gray-700"
              d="M20.1504 9.77117C20.1504 15.7424 15.3322 20.583 9.38852 20.583C5.42179 20.583 1.95632 18.4269 0.0898438 15.2177C10.6335 12.6385 13.5892 4.17369 14.0292 0.0134277C17.6498 1.75446 20.1504 5.46943 20.1504 9.77117Z"
            />
          </g>
          <defs>
            <clipPath id="clip0_3989_10946">
              <rect width="24" height="24" fill="white" />
            </clipPath>
          </defs>
        </svg>
      </a>

      <IconButton v-if="showMenuButton" ref="showTopMenuBtn" :shadow="false" :action="showTopMenu" :active="showHeaderMenu" class="max-tablet:hidden">
        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="6" viewBox="0 0 20 6" fill="none">
          <path
            fill-rule="evenodd"
            clip-rule="evenodd"
            d="M3 5C4.10457 5 5 4.10457 5 3C5 1.89543 4.10457 1 3 1C1.89543 1 1 1.89543 1 3C1 4.10457 1.89543 5 3 5Z"
          />
          <path
            fill-rule="evenodd"
            clip-rule="evenodd"
            d="M10 5C11.1046 5 12 4.10457 12 3C12 1.89543 11.1046 1 10 1C8.89543 1 8 1.89543 8 3C8 4.10457 8.89543 5 10 5Z"
          />
          <path
            fill-rule="evenodd"
            clip-rule="evenodd"
            d="M17 5C18.1046 5 19 4.10457 19 3C19 1.89543 18.1046 1 17 1C15.8954 1 15 1.89543 15 3C15 4.10457 15.8954 5 17 5Z"
          />
        </svg>
      </IconButton>
    </div>

    <DraftHeaderMenu v-if="showHeaderMenu" ref="topHeaderMenuRef" class="max-tablet:hidden" :draft="draft" />

    <div
      v-if="isOwner"
      class="flex flex-wrap items-center gap-x-[12px] gap-y-[6px] rounded-12 text-sm text-black dark:text-gray-500 max-tablet:mt-[16px] max-tablet:border max-tablet:border-[#DBDBDB]/50 max-tablet:bg-[#ECECEC]/80 max-tablet:px-[16px] max-tablet:py-[10px] max-tablet:shadow-[inset_0_0_12px_rgba(255,255,255,0.8)] max-tablet:backdrop-blur-[44px] max-tablet:dark:border-[#2B2B2B]/50 max-tablet:dark:bg-[#1C1C1C]/80 max-tablet:dark:shadow-[inset_0_0_12px_rgba(255,255,255,0.1)] laptop:mr-[12px]"
    >
      <div v-if="showWorkspaceCrumb" class="flex items-center justify-start gap-[10px]">
        <LockIcon stroke-class="stroke-current" fill-class="fill-current" />
        <a class="cursor-pointer" :href="directoryLink">{{ draftView?.workspace_name }}</a>
      </div>
      <div v-if="showWorkspaceCrumb">/</div>

      <div v-if="showProjectCrumb" class="flex items-center justify-start gap-[10px]">
        <TeamIcon class="stroke-current" />
        <a class="cursor-pointer" :href="directoryLink">{{ draftView?.project_name }}</a>
      </div>
      <div v-if="showProjectCrumb">/</div>

      <div>{{ draftView?.name }}</div>
    </div>
  </div>
</template>

<style scoped lang="scss"></style>
